import { collection } from '@/infra/collection'
import {
  Transaction,
  type TransactionProps,
} from '@/features/billing/transactions/Transaction'
import { type BillingApi } from '@/features/billing/api/billing-api'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import type { TransactionResource } from '@/features/billing/api/resources/transaction'
import { safeParseDate } from '@/utils/date-util'
import { match } from '@/utils/pattern-match-util'

/**
 * Store for billing transactions.
 */
export class TransactionStore {
  /**
   * Transaction collection.
   */
  readonly transactions = collection<Transaction>({
    create: () => new Transaction(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  constructor(private readonly _api: BillingApi) {}

  /**
   * Lists transactions.
   *
   * @param query
   * @param signal
   */
  async listTransactions(
    query: ListTransactionsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Transaction>> {
    const response = await this._api.transactions.listTransactions(
      {
        after: query.after,
        limit: query.limit,
        job_id: query.scope.type === 'Job' ? query.scope.jobId : undefined,
      },
      signal,
    )

    return {
      next: response.cursor,
      total: response.total,
      data: this.transactions.set(
        response.data.map(mapTransactionResourceToProps),
      ),
    }
  }
}

/**
 * Used to determine what transactions to query for.
 */
export type TransactionsScope = UserTransactionsScope | JobTransactionsScope

/**
 * Query for all the user's transactions
 */
export interface UserTransactionsScope {
  type: 'User'
}

/**
 * Query for transactions in the context of a job.
 */
export interface JobTransactionsScope {
  type: 'Job'
  jobId: Id
}

export interface ListTransactionsQuery extends CursorPageQuery {
  scope: TransactionsScope
}

function mapTransactionResourceToProps(
  resource: TransactionResource,
): TransactionProps {
  const parseMetaStringField = (prop: string) => {
    const parse = (meta: Record<string, unknown>, prop: string) =>
      prop in meta && typeof meta[prop] === 'string' ? meta[prop] : null

    // If a transaction is a refund, then the metadata contains the charge's metadata
    // as a nested field.
    const innerMeta =
      'metadata' in resource.metadata && resource.metadata !== null
        ? (resource.metadata.metadata as Record<string, unknown>)
        : null

    return innerMeta ? parse(innerMeta, prop) : parse(resource.metadata, prop)
  }

  const jobId = Id(parseMetaStringField('job_id'))
  const jobName = parseMetaStringField('job_name')
  const receiptUrl = parseMetaStringField('receipt_url')

  return {
    id: resource.id,
    amount: resource.amount,
    billingMethodId: resource.billing_method_id,
    dateCreated: safeParseDate(resource.date_created),
    jobId,
    jobName,
    receiptUrl,
    transactionType: match(resource.transaction_type, {
      ADMIN_ADD: 'AdminAdd',
      ADMIN_REMOVE: 'AdminRemove',
      BONUS: 'Bonus',
      DEPOSIT: 'Deposit',
      REFUND: 'Refund',
      PURCHASE: 'Charge',
    }),
  }
}
